import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, NgZone } from '@angular/core';
import { AutotextService } from '../autotext.service';
import { ExamsService } from '../exams.service';
import { LoginService } from '../login.service';
import { textDirection } from '../utils/text-direction';

declare var webkitSpeechRecognition: any;
declare var SpeechRecognitionPhrase: any;

// One field the AI wrote, for the results list
interface AIChange {
  section: string;
  field: string;
  value: string;
}

// Self-contained dictation + AI window. The host passes the exam and reloads its own forms on (saved),
// so the same component can be dropped into other reporting systems.
@Component({
  standalone: false,
  selector: 'app-speech-text',
  templateUrl: './speech-text.component.html',
  styleUrls: ['./speech-text.component.scss'],
})
export class SpeechTextComponent implements OnInit, OnDestroy {
  @Input() examId: number;
  @Input() examType: string;
  // closed or view-only exam: show the saved transcript and requests, no recording or AI
  @Input() readOnly = false;
  @Output() closed = new EventEmitter<null>();
  // fired after the AI saved results to the exam - the host should reload the exam data
  @Output() saved = new EventEmitter<any>();

  static readonly SILENCE_TIMEOUT_MS = 60 * 1000;
  static readonly LANGUAGE_KEY = 'speechText.language';
  static readonly AISYSTEM_KEY = 'speechText.aiSystem';

  languages = [
    { code: 'he-IL', name: 'Hebrew' },
    { code: 'en-US', name: 'English' },
    { code: 'ar-IL', name: 'Arabic' },
    { code: 'ru-RU', name: 'Russian' },
    { code: 'pl-PL', name: 'Polish' },
  ];
  aiSystems = [
    { value: 'Claude', name: 'Claude' },
    { value: 'OpenAI', name: 'OpenAI/GPT' },
    { value: 'Gemini', name: 'Gemini' },
  ];

  // what the window says out loud
  // kept short; the window itself says why it stopped after a minute of silence
  static readonly PHRASES = {
    start: 'Listening',
    stop: 'Listening stopped',
    silence: 'Listening stopped',
  };

  // medical vocabulary for the recognizer: unknown until a session starts, then 'active' or 'unsupported'
  static vocabularyUnsupported = false;
  vocabularyState: 'unknown' | 'active' | 'unsupported' = 'unknown';
  // loaded from the server (prompts/medical_vocabulary.txt) each time the window opens
  private vocabulary: { phrase: string; boost: number }[] = [];

  // the current recognition session (a new one per start), null when not listening
  recognition: any = null;
  speechSupported = true;
  private speechReady = false;
  private voice: SpeechSynthesisVoice = null;
  isRecording = false;
  // true while the doctor wants to record; Chrome ends sessions on its own, so we restart until this is false
  private wantRecording = false;
  private lastSpeechAt = 0;
  private silenceTimer: any = null;
  stoppedForSilence = false;

  transcript = '';
  interimTranscript = '';
  instructions = '';
  languageSelect = 'he-IL';
  AIsystem = 'Claude';
  // what the AI fills: only the Consultation (default, every time the window opens) or the whole exam,
  // which is offered only when the server enables it (config.features.dictationFullExam)
  scope: 'consultation' | 'full' = 'consultation';
  get fullExamAllowed(): boolean {
    return !!this.ls.premissions?.DictationFullExam;
  }

  sendingToAI = false;
  aiElapsedSeconds = 0;
  private aiTimerInterval: any = null;
  aiError = '';
  notEnabled = false;

  // results of the last AI call, and the follow-up change requests
  hasResults = false;
  consultationHtml = '';
  changes: AIChange[] = [];
  unprocessed: string[] = [];
  changeRequest = '';
  history: { type: string; text: string; time: string }[] = [];
  historyLoaded = false;

  constructor(
    public atS: AutotextService,
    private es: ExamsService,
    private ls: LoginService,
    private zone: NgZone
  ) {}

  ngOnInit() {
    this.examId = this.examId || this.es.examID;
    this.examType = this.examType || this.es.ExamType;
    // open-access Bedrock model for testing, offered while the server has config.bedrock.testModel set
    if (this.ls.premissions?.BedrockTestModel)
      this.aiSystems.push({ value: 'ClaudeBedrockTest', name: 'Claude (Bedrock test)' });
    // only the AIs the server allows (config.features.dictationAI); older servers send no list
    const allowed: string[] = this.ls.premissions?.DictationAI;
    if (Array.isArray(allowed) && allowed.length)
      this.aiSystems = this.aiSystems.filter((s) =>
        allowed.includes(s.value.startsWith('Claude') ? 'Claude' : s.value)
      );
    if (!this.aiSystems.some((s) => s.value == this.AIsystem) && this.aiSystems.length)
      this.AIsystem = this.aiSystems[0].value;
    this.languageSelect = this.readPref(SpeechTextComponent.LANGUAGE_KEY) || this.languageSelect;
    const savedSystem = this.readPref(SpeechTextComponent.AISYSTEM_KEY);
    if (this.aiSystems.some((s) => s.value == savedSystem)) this.AIsystem = savedSystem;
    // a closed exam shows only what was saved: no unsent draft, no microphone
    if (!this.readOnly) {
      this.transcript = this.readDraft();
      this.initSpeechRecognition();
      this.atS.getVocabulary().subscribe({
        next: (list) => (this.vocabulary = Array.isArray(list) ? list : []),
        error: (error) => console.log(error),
      });
    }
    this.loadHistory();
  }

  private switchToReadOnly() {
    if (this.isRecording) this.stopDictation();
    this.readOnly = true;
    this.transcript = '';
    this.instructions = '';
    this.loadHistory();
  }

  ngOnDestroy() {
    this.wantRecording = false;
    this.clearSilenceTimer();
    clearInterval(this.aiTimerInterval);
    this.endSession(true);
    try {
      window.speechSynthesis.onvoiceschanged = null;
    } catch {}
  }

  // ---------- dictation ----------

  initSpeechRecognition() {
    if (typeof webkitSpeechRecognition === 'undefined') {
      this.speechSupported = false;
      return;
    }
    this.speechReady = true;
    this.pickVoice();
    // voices often load after the page, so pick again once the browser has them
    try {
      window.speechSynthesis.onvoiceschanged = () => this.pickVoice();
    } catch {}
  }

  // A fresh recognizer for every session. Reusing one across stop/start missed words: stop() finishes
  // in the background, so a quick restart failed silently and the old session's onend restarted it late.
  // Each instance keeps its own provisional text; a stopped one may still deliver its last final words.
  private startSession() {
    const rec = new webkitSpeechRecognition();
    const lastInterim: { [index: string]: string } = {};
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = this.languageSelect;
    this.applyMedicalVocabulary(rec);

    rec.onresult = (event) => {
      this.zone.run(() => {
        let finalTranscript = '';
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const text = result[0].transcript.trim();
          if (result.isFinal) {
            finalTranscript += this.keepEnglishTerms(text, lastInterim[i]) + '\n';
            delete lastInterim[i];
          } else {
            lastInterim[i] = text;
            interim += text + ' ';
          }
        }
        if (rec === this.recognition) {
          this.interimTranscript = interim;
          this.lastSpeechAt = Date.now();
        }
        if (finalTranscript) {
          // a new recording continues the existing transcript
          this.transcript += finalTranscript;
          this.saveDraft();
        }
      });
    };

    rec.onend = () => {
      this.zone.run(() => {
        // Chrome ends sessions on its own; words it had not finalized yet would otherwise be lost
        this.commitPendingInterim(lastInterim);
        if (rec !== this.recognition) return; // an old session finishing after a stop or restart
        this.interimTranscript = '';
        if (this.wantRecording) {
          this.startSession(); // Chrome ended it by itself: carry on in a new one
          return;
        }
        this.recognition = null;
        this.isRecording = false;
        this.clearSilenceTimer();
      });
    };

    rec.onerror = (event) => {
      // the browser has the phrases API but its recognizer can't use it: start() is aborted, and the
      // onend that follows starts a new session, now without the vocabulary
      if (event.error == 'phrases-not-supported') {
        SpeechTextComponent.vocabularyUnsupported = true;
        this.zone.run(() => (this.vocabularyState = 'unsupported'));
        console.log('Speech recognition: medical vocabulary not supported here - continuing without it');
        return;
      }
      // 'no-speech' just ends the session, onend restarts it; anything else stops recording
      if (event.error == 'not-allowed' || event.error == 'service-not-allowed' || event.error == 'audio-capture') {
        this.zone.run(() => {
          this.wantRecording = false;
          this.aiError = 'The microphone is not available - please allow microphone access in the browser.';
        });
      }
    };

    this.recognition = rec;
    this.lastSpeechAt = Date.now();
    try {
      rec.start();
    } catch (e) {
      console.log(e);
    }
  }

  // Contextual biasing (SpeechRecognition.phrases, experimental): tell the recognizer which English medical
  // terms to expect, so multi-word terms are not dropped from Hebrew dictation. Skipped where the browser
  // has no such API, or once its recognizer has refused it (remembered for the page's lifetime).
  private applyMedicalVocabulary(rec: any) {
    if (
      SpeechTextComponent.vocabularyUnsupported ||
      typeof SpeechRecognitionPhrase === 'undefined' ||
      !('phrases' in rec)
    ) {
      this.vocabularyState = 'unsupported';
      return;
    }
    if (!this.vocabulary.length) return; // not loaded (yet), or the file is empty
    try {
      rec.phrases = this.vocabulary.map((p) => new SpeechRecognitionPhrase(p.phrase, p.boost));
      this.vocabularyState = 'active';
    } catch (e) {
      console.log('Speech recognition: could not set the medical vocabulary', e);
      this.vocabularyState = 'unsupported';
    }
  }

  // stop() keeps delivering the words already heard; abort() drops them (used by Clear)
  private endSession(discard = false) {
    const rec = this.recognition;
    this.recognition = null;
    this.interimTranscript = '';
    if (!rec) return;
    if (discard) rec.onresult = rec.onend = null;
    try {
      if (discard) rec.abort();
      else rec.stop();
    } catch {}
  }

  // Hebrew recognition often shows an English medical term ("corpus callosum") while listening and then
  // drops it from the final text. If the final lost Latin-script words the last guess had, keep the guess.
  private keepEnglishTerms(finalText: string, interimText: string): string {
    if (!interimText || this.languageSelect.startsWith('en')) return finalText;
    const latinWords = (s: string) => (s.match(/[A-Za-z][A-Za-z'-]*/g) || []).map((w) => w.toLowerCase());
    const finalWords = new Set(latinWords(finalText));
    const dropped = latinWords(interimText).filter((w) => !finalWords.has(w));
    return dropped.length ? interimText : finalText;
  }

  private commitPendingInterim(lastInterim: { [index: string]: string }) {
    const pending = Object.keys(lastInterim)
      .sort((a, b) => +a - +b)
      .map((k) => lastInterim[k])
      .filter(Boolean)
      .join(' ')
      .trim();
    for (const k of Object.keys(lastInterim)) delete lastInterim[k];
    if (pending) {
      this.transcript += pending + '\n';
      this.saveDraft();
    }
  }

  toggleDictation() {
    if (this.isRecording) this.stopDictation();
    else this.startDictation();
  }

  startDictation() {
    if (!this.speechReady || this.isRecording) return;
    this.aiError = '';
    this.stoppedForSilence = false;
    this.writePref(SpeechTextComponent.LANGUAGE_KEY, this.languageSelect);
    if (this.transcript && !this.transcript.endsWith('\n')) this.transcript += '\n';
    this.wantRecording = true;
    this.isRecording = true;
    this.lastSpeechAt = Date.now();
    this.startSilenceTimer();
    // say it first and listen after: talking over the doctor's first words made Chrome miss them
    // (echo cancellation), which is why only the first recording - when the mic is slow to open - worked
    this.announce(SpeechTextComponent.PHRASES.start, () => {
      if (this.wantRecording && !this.recognition) this.startSession();
    });
  }

  stopDictation(announcement = SpeechTextComponent.PHRASES.stop) {
    this.wantRecording = false;
    this.clearSilenceTimer();
    this.endSession();
    this.isRecording = false;
    this.announce(announcement);
  }

  onLanguageChange() {
    this.writePref(SpeechTextComponent.LANGUAGE_KEY, this.languageSelect);
    // switch to a session in the new language straight away; the old one still delivers its last words
    if (this.isRecording && this.recognition) {
      this.endSession();
      this.startSession();
    }
  }

  // stop by itself after a minute without speech
  private startSilenceTimer() {
    this.clearSilenceTimer();
    this.silenceTimer = setInterval(() => {
      if (this.isRecording && Date.now() - this.lastSpeechAt > SpeechTextComponent.SILENCE_TIMEOUT_MS) {
        this.stoppedForSilence = true;
        this.stopDictation(SpeechTextComponent.PHRASES.silence);
      }
    }, 2000);
  }

  private clearSilenceTimer() {
    if (this.silenceTimer) clearInterval(this.silenceTimer);
    this.silenceTimer = null;
  }

  clearTranscript() {
    if (!this.transcript || !confirm('Clear the dictated text?')) return;
    if (this.isRecording) {
      // abort, not stop: stop() would still deliver the pending words after the clear
      this.wantRecording = false;
      this.clearSilenceTimer();
      this.endSession(true);
      this.isRecording = false;
      this.announce(SpeechTextComponent.PHRASES.stop);
    }
    this.transcript = '';
    this.interimTranscript = '';
    this.saveDraft();
  }

  // Spoken prompts, in a British voice. done() runs when the prompt has finished
  // (or after a short fallback, since some browsers never fire onend for speech).
  private announce(text: string, done: () => void = null) {
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      if (done) this.zone.run(done);
    };
    try {
      window.speechSynthesis.cancel(); // never queue behind a previous prompt
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-GB';
      if (this.voice) utterance.voice = this.voice;
      utterance.onend = finish;
      utterance.onerror = finish;
      window.speechSynthesis.speak(utterance);
      setTimeout(finish, 2500);
    } catch {
      finish();
    }
  }

  // A British English voice: the one the window used before (Chrome), the natural-sounding Edge ones,
  // then any en-GB voice.
  private pickVoice() {
    try {
      const voices = window.speechSynthesis.getVoices();
      const gb = voices.filter((v) => v.lang == 'en-GB' || v.lang == 'en_GB');
      const preferred = [
        'Google UK English Male',
        'Microsoft Ryan Online (Natural)',
        'Microsoft Sonia Online (Natural)',
        'Google UK English Female',
        'Daniel',
        'Microsoft George',
      ];
      this.voice =
        preferred.map((name) => gb.find((v) => v.name.startsWith(name))).find(Boolean) || gb[0] || null;
    } catch {
      this.voice = null;
    }
  }

  // ---------- AI ----------

  sendToAI() {
    if (!this.transcript.trim()) return;
    if (this.isRecording) this.stopDictation();
    this.runAI(
      this.atS.sendToAI(
        this.transcript,
        this.AIsystem,
        this.examType,
        this.examId,
        this.instructions,
        this.languageSelect,
        this.scope
      ),
      true
    );
  }

  requestChanges() {
    if (!this.changeRequest.trim()) return;
    this.runAI(
      this.atS.refineAI(this.changeRequest, this.AIsystem, this.examType, this.examId, this.scope),
      false
    );
  }

  private runAI(request, isNewResult: boolean) {
    this.writePref(SpeechTextComponent.AISYSTEM_KEY, this.AIsystem);
    this.sendingToAI = true;
    this.aiError = '';
    this.aiElapsedSeconds = 0;
    clearInterval(this.aiTimerInterval);
    this.aiTimerInterval = setInterval(() => this.aiElapsedSeconds++, 1000);
    request.subscribe({
      next: (data) => {
        this.finishAI();
        if (!data || data.error) {
          this.aiError = 'The AI result was not valid. Please try again' + (isNewResult ? '.' : ' or rephrase the request.');
          return;
        }
        this.showResults(data, isNewResult);
        if (isNewResult) this.saveDraft(true);
        else this.changeRequest = '';
        this.loadHistory();
        this.saved.emit(data);
      },
      error: (error) => {
        this.finishAI();
        // the server refuses AI changes to a closed exam (the page may have been open since before 3:00)
        if (error.status == 403 && error.error?.closed) this.switchToReadOnly();
        else if (error.status == 403) this.notEnabled = true;
        else this.aiError = 'Could not reach the AI service. Please try again.';
        console.log(error);
      },
    });
  }

  private finishAI() {
    clearInterval(this.aiTimerInterval);
    this.sendingToAI = false;
  }

  // turn the model's JSON into a readable list of what was written to the exam
  private showResults(data, isNewResult: boolean) {
    const changes: AIChange[] = [];
    let consultation = null;
    for (const section of Object.keys(data)) {
      const value = data[section];
      if (section == 'unprocessed' && Array.isArray(value)) {
        this.unprocessed = isNewResult ? value : this.unprocessed;
        continue;
      }
      if (!value || typeof value != 'object' || Array.isArray(value)) continue;
      for (const field of Object.keys(value)) {
        if (field == 'InstanceNumber') continue;
        if (field == 'Consultation') {
          consultation = value[field];
          continue;
        }
        const fetus = value['InstanceNumber'] > 1 ? ` (fetus ${value['InstanceNumber']})` : '';
        changes.push({ section: this.sectionName(section) + fetus, field, value: this.displayValue(value[field]) });
      }
    }
    if (consultation !== null) this.consultationHtml = consultation || '';
    else if (isNewResult) this.consultationHtml = '';
    // a change request only returns what changed, so add to the list instead of replacing it
    this.changes = isNewResult ? changes : this.mergeChanges(this.changes, changes);
    if (isNewResult && Array.isArray(data.unprocessed) === false) this.unprocessed = [];
    this.hasResults = true;
  }

  consultationDir() {
    return textDirection(this.consultationHtml) || 'auto';
  }

  private mergeChanges(current: AIChange[], updates: AIChange[]) {
    const merged = current.filter((c) => !updates.some((u) => u.section == c.section && u.field == c.field));
    return merged.concat(updates);
  }

  private sectionName(section: string) {
    const names = { exams: 'Pregnancy', pregnancy: 'Pregnancy', cns: 'CNS', nt: 'NT' };
    return names[section.toLowerCase()] || section.charAt(0).toUpperCase() + section.slice(1);
  }

  private displayValue(value) {
    if (value === null || value === '') return '(cleared)';
    if (typeof value == 'object') return JSON.stringify(value);
    return String(value);
  }

  // saved transcripts for this exam: restores the last dictation and shows what was asked so far
  private loadHistory() {
    if (!this.examId) return;
    this.atS.getTranscripts(this.examId).subscribe({
      next: (rows: any[]) => {
        this.history = (rows || []).map((r) => ({
          type: r.RequestType == 'refine' ? 'Change request' : 'Sent to AI',
          text: r.RequestType == 'refine' ? r.Instructions : r.Instructions ? 'Instructions: ' + r.Instructions : '',
          time: r.CreationTime,
        }));
        const lastProcess = [...(rows || [])].reverse().find((r) => r.RequestType == 'process');
        if (lastProcess) {
          this.hasResults = true;
          if (!this.transcript) this.transcript = lastProcess.Transcript || '';
          if (!this.instructions) this.instructions = lastProcess.Instructions || '';
        }
        this.historyLoaded = true;
      },
      error: (error) => {
        if (error.status == 403) this.notEnabled = true;
        this.historyLoaded = true;
      },
    });
  }

  // ---------- local persistence (draft survives closing the window) ----------

  private draftKey() {
    return 'speechText.draft.' + this.examId;
  }

  saveDraft(sent = false) {
    try {
      if (sent || !this.transcript) sessionStorage.removeItem(this.draftKey());
      else sessionStorage.setItem(this.draftKey(), this.transcript);
    } catch {}
  }

  private readDraft() {
    try {
      return sessionStorage.getItem(this.draftKey()) || '';
    } catch {
      return '';
    }
  }

  private readPref(key: string) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writePref(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
    } catch {}
  }
}
